import React, { useEffect, useRef } from 'react';
import { usePanelLabel } from '../primitives/labels';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../primitives/Tabs';
import { Tooltip, TooltipTrigger, TooltipContent } from '../primitives/Tooltip';
import { X } from '../primitives/icons';
import { DropZone } from './SidePanelCore';
import { globalDragState } from './globalDragState';
import '../theme/minimized-sidepanel.css';

/** Icon-only breakpoint for sidepanel tabs; mirrors the
    `@container panel-tab (width < 50px)` rule in side-panel.css. */
const TAB_ICON_ONLY_WIDTH = 50;

/** Tabs wide enough to show their label inline don't need the hover
    tooltip, so it is gated to icon-only tabs. The width is read as the
    content box, matching what the container query measures. */
const shouldOpenTabTooltip = (trigger: HTMLElement | null): boolean => {
    if (!trigger) return false;
    const style = getComputedStyle(trigger);
    return (
        trigger.clientWidth -
            parseFloat(style.paddingLeft) -
            parseFloat(style.paddingRight) <
        TAB_ICON_ONLY_WIDTH
    );
};

export interface RegularSidePanelProps {
    isHorizontal: boolean;
    visibleGroups: any[];
    activeDropZone: string | null;
    handleDropZoneDragOver: (
        e: React.DragEvent<HTMLDivElement>,
        zoneId: string,
    ) => void;
    handleDropZoneDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
    handleDropOnZone: (
        e: React.DragEvent<HTMLDivElement>,
        groupId: string,
        position: 'before' | 'after',
    ) => void;
    handleGroupResize: (
        groupId: string,
        currentHeight: number,
    ) => (e: React.MouseEvent) => void;
    handleHorizontalGroupResize?: (
        index: number,
    ) => (e: React.MouseEvent) => void;
    getPanelGroupStyle: (group: any, index: number) => React.CSSProperties;
    toggleGroupMinimize: (groupId: string) => void;
    autoCollapseContainerRef?: React.Ref<HTMLDivElement>;
    activeTab: string;
    setActiveTab: (tabId: string) => void;
    tabDropPosition: Record<string, 'left' | 'right' | null>;
    handleCloseTab: (tabId: string, groupId: string) => void;
    panelId: string;
    handleTabMouseDown?: (
        e: React.MouseEvent,
        tabId: string,
        groupId: string,
    ) => void;
}
export const RegularSidePanel: React.FC<RegularSidePanelProps> = ({
    isHorizontal,
    visibleGroups,
    activeDropZone,
    handleDropZoneDragOver,
    handleDropZoneDragLeave,
    handleDropOnZone,
    handleGroupResize,
    handleHorizontalGroupResize,
    getPanelGroupStyle,
    toggleGroupMinimize,
    autoCollapseContainerRef,
    activeTab,
    setActiveTab,
    tabDropPosition,
    handleCloseTab,
    panelId,
    handleTabMouseDown,
}) => {
    const t = usePanelLabel();
    const tabsListRef = useRef<HTMLDivElement | null>(null);

    // Vertical wheel scrolls the tab strip horizontally when it overflows.
    // React registers onWheel as passive, so a native non-passive listener is
    // required for preventDefault to stop the page-level scroll chain.
    useEffect(() => {
        const el = tabsListRef.current;
        if (!el) return;
        const handleWheel = (e: WheelEvent) => {
            const maxScroll = el.scrollWidth - el.clientWidth;
            if (maxScroll <= 0) return;
            const delta =
                Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
            if (delta === 0) return;
            const next = el.scrollLeft + delta;
            if (next < 0 || next > maxScroll) return;
            e.preventDefault();
            el.scrollLeft = next;
        };
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, []);
    const onTabMouseDown =
        handleTabMouseDown ||
        ((e: React.MouseEvent, tabId: string, groupId: string) => {
            const tab = visibleGroups
                .flatMap((g) => g.tabs)
                .find((tabItem) => tabItem.id === tabId);
            globalDragState.setState({
                isDragging: true,
                sourceType: 'regular',
                sourcePanelId: panelId,
                sourceGroupId: groupId,
                sourceTabId: tabId,
                position: { x: e.clientX, y: e.clientY },
                tabData: tab,
                dragGhostIcon: tab?.icon,
                dragGhostPos: { x: e.clientX, y: e.clientY },
                ghostType: 'regular',
                ghostData: {
                    panelId,
                    groupId,
                    tabId,
                    label: tab?.labelKey
                        ? t(tab.labelKey, tab.label)
                        : tab?.label,
                },
            });
            const handleMouseUp = () => {
                globalDragState.clear();
                window.removeEventListener('mouseup', handleMouseUp);
            };
            window.addEventListener('mouseup', handleMouseUp);
        });
    // Groups auto-collapsed by the accordion system render as header-only
    // rows: hide their content panes and resize handles without touching the
    // stored heights, so restoring them brings the previous size back.
    const collapsedGroups = React.useMemo(() => {
        if (isHorizontal) return new Set<string>();
        return new Set(
            visibleGroups
                .filter((group: any) => group.isMinimized)
                .map((group: any) => group.id as string),
        );
    }, [isHorizontal, visibleGroups]);
    return (
        <div
            className={`panel-groups${isHorizontal ? ' horizontal' : ''}`}
            ref={autoCollapseContainerRef}
        >
            {/* VERTICAL: İlk grubun başında DropZone */}
            {!isHorizontal && visibleGroups.length > 0 && (
                <DropZone
                    position="before"
                    groupId={visibleGroups[0].id}
                    isOver={activeDropZone === `before-${visibleGroups[0].id}`}
                    onDragOver={(e: React.DragEvent<HTMLDivElement>) =>
                        handleDropZoneDragOver(
                            e,
                            `before-${visibleGroups[0].id}`,
                        )
                    }
                    onDragLeave={(e: React.DragEvent<HTMLDivElement>) =>
                        handleDropZoneDragLeave(e)
                    }
                    onDrop={(e: React.DragEvent<HTMLDivElement>) =>
                        handleDropOnZone(e, visibleGroups[0].id, 'before')
                    }
                />
            )}
            {visibleGroups.map((group: any, groupIndex: number) => (
                <React.Fragment key={`group-${group.id}-${groupIndex}`}>
                    <div
                        className={`panel-group${group.isMinimized || collapsedGroups.has(group.id) ? ' minimized' : ''}${isHorizontal ? ' horizontal' : ''}`}
                        data-group-id={group.id}
                        style={getPanelGroupStyle(group, groupIndex)}
                    >
                        <Tabs
                            value={
                                group.tabs.find(
                                    (tab: any) => tab.id === activeTab,
                                )?.id || group.tabs[0]?.id
                            }
                            onValueChange={setActiveTab}
                            className="panel-group-tabs"
                        >
                            <TabsList
                                ref={tabsListRef}
                                className={`panel-tabs-list scrollable-tabs`}
                                onDoubleClick={() =>
                                    toggleGroupMinimize(group.id)
                                }
                            >
                                {group.tabs.map(
                                    (tab: any, tabIndex: number) => {
                                        const tabContent = (
                                            <>
                                                {tab.icon}
                                                <span className="tab-label">
                                                    {tab.labelKey
                                                        ? t(tab.labelKey,
                                                              tab.label,
                                                          )
                                                        : tab.label}
                                                </span>
                                                <span
                                                    className="tab-close focus-ring"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Close ${tab.labelKey ? t(tab.labelKey, tab.label) : tab.label}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCloseTab(
                                                            tab.id,
                                                            group.id,
                                                        );
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key !== 'Enter' && e.key !== ' ') return;
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleCloseTab(
                                                            tab.id,
                                                            group.id,
                                                        );
                                                    }}
                                                >
                                                    <X size={14} />
                                                </span>
                                            </>
                                        );
                                        return (
                                            <Tooltip
                                                key={`tab-${group.id}-${tab.id}-${tabIndex}`}
                                                shouldOpen={
                                                    shouldOpenTabTooltip
                                                }
                                            >
                                                <TooltipTrigger asChild>
                                                    <TabsTrigger
                                                        data-tab-id={tab.id}
                                                        value={tab.id}
                                                        className={[
                                                            'panel-tab',
                                                            tabDropPosition[
                                                                tab.id
                                                            ] === 'left'
                                                                ? 'drop-target-left'
                                                                : '',
                                                            tabDropPosition[
                                                                tab.id
                                                            ] === 'right'
                                                                ? 'drop-target-right'
                                                                : '',
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' ')}
                                                        onMouseDown={(
                                                            e: React.MouseEvent,
                                                        ) => {
                                                            // Single click on a collapsed group's header row
                                                            // expands it (accordion behavior). The guard flag
                                                            // swallows the trailing dblclick that would
                                                            // otherwise re-collapse the freshly opened group.
                                                            if (
                                                                !isHorizontal &&
                                                                group.isMinimized
                                                            ) {
                                                                toggleGroupMinimize(
                                                                    group.id,
                                                                );
                                                                (
                                                                    e.currentTarget as HTMLElement
                                                                ).dataset.justExpanded =
                                                                    '1';
                                                                window.setTimeout(
                                                                    () => {
                                                                        delete (
                                                                            e.currentTarget as HTMLElement | null
                                                                        )
                                                                            ?.dataset
                                                                            ?.justExpanded;
                                                                    },
                                                                    400,
                                                                );
                                                                return;
                                                            }
                                                            if (
                                                                !isHorizontal &&
                                                                (
                                                                    e.currentTarget as HTMLElement
                                                                ).dataset
                                                                    .justExpanded
                                                            ) {
                                                                return;
                                                            }
                                                            onTabMouseDown(
                                                                e,
                                                                tab.id,
                                                                group.id,
                                                            );
                                                        }}
                                                    >
                                                        {tabContent}
                                                    </TabsTrigger>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    side="top"
                                                    className="tooltip-content"
                                                >
                                                    {tab.labelKey
                                                        ? t(tab.labelKey,
                                                              tab.label,
                                                          )
                                                        : tab.label}
                                                </TooltipContent>
                                            </Tooltip>
                                        );
                                    },
                                )}
                            </TabsList>
                            {!group.isMinimized &&
                                !collapsedGroups.has(group.id) &&
                                group.tabs.map((tab: any) => (
                                    <TabsContent
                                        key={tab.id}
                                        value={tab.id}
                                        className="panel panel-tab-content"
                                    >
                                        {tab.content}
                                    </TabsContent>
                                ))}
                        </Tabs>
                    </div>
                    {/* Vertical sub-group resize handle: rendered between adjacent groups (splitter) */}
                    {!isHorizontal &&
                        groupIndex < visibleGroups.length - 1 &&
                        !group.isMinimized &&
                        !collapsedGroups.has(group.id) && (
                            <div
                                className="group-resize-handle"
                                onMouseDown={handleGroupResize(
                                    group.id,
                                    group.height || 120,
                                )}
                            >
                                <div className="group-resize-bar"></div>
                            </div>
                        )}
                    {/* DropZone: Her grubun sonuna (son grup hariç) */}
                    {groupIndex < visibleGroups.length - 1 &&
                        (isHorizontal ? (
                            <div className="horizontal-group-dropzone-wrapper">
                                <DropZone
                                    position="after"
                                    groupId={group.id}
                                    isOver={
                                        activeDropZone === `after-${group.id}`
                                    }
                                    onDragOver={(
                                        e: React.DragEvent<HTMLDivElement>,
                                    ) =>
                                        handleDropZoneDragOver(
                                            e,
                                            `after-${group.id}`,
                                        )
                                    }
                                    onDragLeave={(
                                        e: React.DragEvent<HTMLDivElement>,
                                    ) => handleDropZoneDragLeave(e)}
                                    onDrop={(
                                        e: React.DragEvent<HTMLDivElement>,
                                    ) => handleDropOnZone(e, group.id, 'after')}
                                />
                                <div
                                    className="horizontal-group-resize-handle"
                                    onMouseDown={
                                        handleHorizontalGroupResize
                                            ? handleHorizontalGroupResize(
                                                  groupIndex,
                                              )
                                            : undefined
                                    }
                                >
                                    <div className="horizontal-group-resize-bar"></div>
                                </div>
                            </div>
                        ) : (
                            <DropZone
                                position="after"
                                groupId={group.id}
                                isOver={activeDropZone === `after-${group.id}`}
                                onDragOver={(
                                    e: React.DragEvent<HTMLDivElement>,
                                ) =>
                                    handleDropZoneDragOver(
                                        e,
                                        `after-${group.id}`,
                                    )
                                }
                                onDragLeave={(
                                    e: React.DragEvent<HTMLDivElement>,
                                ) => handleDropZoneDragLeave(e)}
                                onDrop={(e: React.DragEvent<HTMLDivElement>) =>
                                    handleDropOnZone(e, group.id, 'after')
                                }
                            />
                        ))}
                </React.Fragment>
            ))}
            {/* VERTICAL: Son grubun sonuna DropZone (overlays the panel
                bottom edge — see .group-drop-zone.trailing in CSS) */}
            {!isHorizontal && visibleGroups.length > 0 && (
                <DropZone
                    position="after"
                    trailing={true}
                    groupId={visibleGroups[visibleGroups.length - 1].id}
                    isOver={
                        activeDropZone ===
                        `after-${visibleGroups[visibleGroups.length - 1].id}-last`
                    }
                    onDragOver={(e: React.DragEvent<HTMLDivElement>) =>
                        handleDropZoneDragOver(
                            e,
                            `after-${visibleGroups[visibleGroups.length - 1].id}-last`,
                        )
                    }
                    onDragLeave={(e: React.DragEvent<HTMLDivElement>) =>
                        handleDropZoneDragLeave(e)
                    }
                    onDrop={(e: React.DragEvent<HTMLDivElement>) =>
                        handleDropOnZone(
                            e,
                            visibleGroups[visibleGroups.length - 1].id,
                            'after',
                        )
                    }
                />
            )}
        </div>
    );
};
